import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { ListenButton } from "@/components/AiNote";
import { CalendarHeart } from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/Btn";
import { Spinner } from "@/components/Brand";
import { NoteHeader } from "@/components/AiNote";
import { api, errText } from "@/lib/api";
import { usePrefs, useSavePrefs } from "@/lib/store";

const WEEK = 7 * 86400000;

const wkDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

function PastWeeks({ current }) {
  const { data = [] } = useQuery({ queryKey: ["weekly-summaries"], queryFn: async () => (await api.get("/weekly-summaries")).data });
  const [open, setOpen] = useState(null);
  const past = data.filter((w) => w.at !== current?.at);
  if (!past.length) return null;
  return (
    <div className="mt-4 border-t border-t-border pt-3" data-testid="past-weeks">
      <p className="tf-tag mb-2">Past weeks</p>
      <ul className="flex flex-col gap-1.5 max-h-80 overflow-y-auto pr-1">
        {past.map((w, i) => (
          <li key={w.id} className="rounded-2xl bg-t-surface/70 border border-t-border">
            <button onClick={() => setOpen(open === w.id ? null : w.id)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left" data-testid={`past-week-${i}`}>
              <span className="text-sm font-semibold text-t-on">Week to {wkDate(w.at)} <span className="font-normal text-t-muted">· {w.checkins} check-in{w.checkins === 1 ? "" : "s"}</span></span>
              <ChevronDown size={16} className={`text-t-muted transition-transform duration-200 ${open === w.id ? "rotate-180" : ""}`} />
            </button>
            {open === w.id ? (
              <div className="px-4 pb-3 tf-rise" data-testid={`past-week-text-${i}`}>
                <ListenButton text={w.text} testId={`past-week-listen-${i}`} />
                <p className="mt-2 text-sm text-t-on leading-relaxed whitespace-pre-line">{w.text}</p>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function WeeklySummary() {
  const qc = useQueryClient();
  const { data: prefs, isFetched } = usePrefs();
  const save = useSavePrefs();
  const [loading, setLoading] = useState(false);
  const tried = useRef(false);
  const w = prefs?.weeklySummary;
  const stale = !w || Date.now() - new Date(w.at).getTime() > WEEK;
  const write = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/weekly-summary", {}, { timeout: 90000 });
      save.mutate({ weeklySummary: data });
      qc.invalidateQueries({ queryKey: ["weekly-summaries"] });
    } catch (e) { toast.error(errText(e)); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (isFetched && stale && !tried.current) { tried.current = true; write(); }
  }, [isFetched, stale]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="rounded-[20px] bg-t-brand2 p-5" data-testid="weekly-summary">
      <NoteHeader autoPlay={false} testId="weekly-summary-listen" text={loading ? null : w?.text}
        title={<p className="flex items-center gap-2 font-bold text-t-onbrand2"><CalendarHeart size={17} /> Your week, gently</p>} />
      {loading ? <div className="mt-3 flex items-center gap-3 text-t-on2 text-sm"><Spinner className="w-4 h-4 text-t-brand" /> Looking back over your last 7 days…</div>
        : w ? <p className="mt-3 text-t-on leading-relaxed whitespace-pre-line" data-testid="weekly-summary-text">{w.text}</p> : null}
      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        {w && !loading ? <span className="text-xs text-t-muted" data-testid="weekly-summary-date">Written {new Date(w.at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · also shared at the top of your reports</span> : <span />}
        <Btn size="md" variant="secondary" label="Refresh" onClick={write} disabled={loading} testId="weekly-summary-refresh" />
      </div>
      <PastWeeks current={w} />
    </div>
  );
}
